import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Save, Bookmark, Trash2, UserCog } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { RichBodyEditor } from "@/components/manual/RichBodyEditor";

// -----------------------------------------------------------------------------
// <Section> — collapsible section wrapper used across Manual Compose.
// -----------------------------------------------------------------------------
export function Section({ title, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section>
      <button type="button" onClick={()=>setOpen(!open)}
        className="w-full flex items-center gap-2 mb-3 text-left"
        data-testid={`mc-section-toggle-${title.toLowerCase().replace(/[^a-z]+/g,"-")}`}>
        {open ? <ChevronDown className="w-4 h-4 opacity-70"/> : <ChevronRight className="w-4 h-4 opacity-70"/>}
        <span className="text-xs uppercase tracking-[0.25em] text-[color:var(--text-muted)]">{title}</span>
      </button>
      {open && children}
    </section>
  );
}

// -----------------------------------------------------------------------------
// <SectionCard> — one canonical front/back-matter row with include toggle,
// heading editor, content textarea, and inline "save as preset" controls.
// -----------------------------------------------------------------------------
export function SectionCard({ section, testIdPrefix, kind, presets, onChange, onSavePreset, onDeletePreset }) {
  const testId = `${testIdPrefix}-${section.key}`;
  const [showPresetSave, setShowPresetSave] = useState(false);
  const [presetName, setPresetName] = useState("");

  const matchingPresets = useMemo(() => {
    const byKind = (presets || []).filter((p) => p.kind === kind);
    const exact = byKind.filter((p) => p.key === section.key);
    const other = byKind.filter((p) => p.key !== section.key);
    return { exact, other };
  }, [presets, kind, section.key]);

  const totalPresets = matchingPresets.exact.length + matchingPresets.other.length;

  const applyPreset = (p) => {
    onChange({ title: p.title, content: p.content, include: true });
    toast.success(`Loaded preset: ${p.name}`);
  };

  const handleSavePreset = async () => {
    const saved = await onSavePreset(presetName);
    if (saved) { setPresetName(""); setShowPresetSave(false); }
  };

  return (
    <div className={`border border-[color:var(--border)] p-4 transition-colors ${section.include ? "bg-white" : "bg-[color:var(--canvas)]/60"}`}
         data-testid={testId}>
      <div className="flex items-center justify-between gap-3">
        <label className="flex items-center gap-3 cursor-pointer flex-1 min-w-0">
          <input type="checkbox" checked={!!section.include}
            onChange={(e)=>onChange({ include: e.target.checked })}
            className="mt-0" data-testid={`${testId}-include`}/>
          <span className={`font-medium text-sm truncate ${section.include ? "" : "text-[color:var(--text-muted)]"}`}>
            {section.title}
          </span>
        </label>
        {totalPresets > 0 && (
          <PresetPicker
            testId={`${testId}-presets`}
            exact={matchingPresets.exact}
            other={matchingPresets.other}
            onPick={applyPreset}
            onDelete={onDeletePreset}
          />
        )}
      </div>
      {section.include && (
        <div className="mt-3 pl-6 space-y-2">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest mb-1 text-[color:var(--text-muted)]">
              Section heading (edit if needed)
            </span>
            <input value={section.title} onChange={(e)=>onChange({ title: e.target.value })}
              className="w-full border border-[color:var(--border)] px-3 py-2 focus:outline-none focus:border-[color:var(--primary)] text-sm"
              data-testid={`${testId}-title`}/>
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest mb-1 text-[color:var(--text-muted)]">Content</span>
            {/* iter-110 — switched from a plain <textarea> to the same
                RichBodyEditor the Introduction and chapters use so
                authors can bold / italic / add bullets / insert links
                in About the Author, Resources, Disclaimers, etc.
                Both backend serializers (`_manual_paragraphs_html`
                and `_epub_paragraphs_html`) autodetect inline HTML
                and sanitize via bleach, so formatting flows end-to-end
                to PDF + EPUB. */}
            <RichBodyEditor
              value={section.content || ""}
              onChange={(v) => onChange({ content: v })}
              testId={`${testId}-content`}
              minHeight={160}
              placeholder="Paste the content for this section…"
            />
          </label>
          <div className="flex items-center gap-2 pt-1">
            {section.key === "about_author_front" && (
              <AuthorProfilePrefill
                testId={`${testId}-author-prefill`}
                isEmpty={!(section.content || "").trim()}
                onApply={(bio, displayName) => {
                  onChange({
                    content: bio,
                    // Only override the heading when it's still the
                    // default so authors who customized it keep it.
                    title: (section.title === "About the Author (Front)" && displayName)
                      ? `About ${displayName}` : section.title,
                    include: true,
                  });
                  toast.success("Filled from your author profile");
                }}
              />
            )}
            {!showPresetSave && section.content?.trim() && (
              <button type="button" onClick={()=>{ setPresetName(section.title); setShowPresetSave(true); }}
                className="text-xs inline-flex items-center gap-1 text-[color:var(--primary)] hover:underline"
                data-testid={`${testId}-save-preset-btn`}>
                <Save className="w-3 h-3"/> Save as preset (reuse in other books)
              </button>
            )}
            {showPresetSave && (
              <div className="flex items-center gap-2 flex-1" data-testid={`${testId}-save-preset-form`}>
                <input value={presetName} onChange={(e)=>setPresetName(e.target.value)}
                  placeholder="Preset name (e.g. Standard AI Disclosure)"
                  className="flex-1 border border-[color:var(--border)] px-2 py-1 text-xs focus:outline-none focus:border-[color:var(--primary)]"
                  data-testid={`${testId}-preset-name`}/>
                <button type="button" onClick={handleSavePreset}
                  className="text-xs px-2 py-1 bg-[color:var(--primary)] text-white"
                  data-testid={`${testId}-save-preset-confirm`}>Save</button>
                <button type="button" onClick={()=>setShowPresetSave(false)}
                  className="text-xs px-2 py-1 border border-[color:var(--border)]">Cancel</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Load-preset dropdown for a section card.
function PresetPicker({ testId, exact, other, onPick, onDelete }) {
  const [open, setOpen] = useState(false);
  const total = exact.length + other.length;
  return (
    <div className="relative">
      <button type="button" onClick={()=>setOpen(!open)}
        className="text-xs inline-flex items-center gap-1 px-2 py-1 border border-[color:var(--border)] hover:bg-[color:var(--canvas)]"
        data-testid={`${testId}-toggle`}>
        <Bookmark className="w-3 h-3"/> Load preset ({total})
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-20 min-w-[260px] bg-white border border-[color:var(--border)] shadow-lg"
             data-testid={`${testId}-menu`}>
          {exact.length > 0 && (
            <div className="p-1">
              <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] px-2 py-1">For this section</p>
              {exact.map((p) => (
                <PresetRow key={p.id} preset={p}
                  onPick={()=>{ onPick(p); setOpen(false); }}
                  onDelete={()=>onDelete(p.id)}
                  testId={`${testId}-preset-${p.id}`}/>
              ))}
            </div>
          )}
          {other.length > 0 && (
            <div className="p-1 border-t border-[color:var(--border)]">
              <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] px-2 py-1">Other ({other[0].kind})</p>
              {other.map((p) => (
                <PresetRow key={p.id} preset={p}
                  onPick={()=>{ onPick(p); setOpen(false); }}
                  onDelete={()=>onDelete(p.id)}
                  testId={`${testId}-preset-${p.id}`}/>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PresetRow({ preset, onPick, onDelete, testId }) {
  return (
    <div className="group flex items-center justify-between px-2 py-1 hover:bg-[color:var(--canvas)] cursor-pointer" data-testid={testId}>
      <button type="button" onClick={onPick} className="text-left flex-1 text-xs min-w-0" data-testid={`${testId}-pick`}>
        <div className="truncate font-medium">{preset.name}</div>
        <div className="truncate text-[color:var(--text-muted)]">{(preset.content || "").slice(0, 80)}</div>
      </button>
      <button type="button" onClick={onDelete}
        className="p-1 opacity-60 group-hover:opacity-100 text-[color:var(--text-muted)] hover:text-red-600"
        title="Delete preset" data-testid={`${testId}-delete`}>
        <Trash2 className="w-3 h-3"/>
      </button>
    </div>
  );
}


// -----------------------------------------------------------------------------
// AuthorProfilePrefill (iter-52) — a small chip that only renders on the
// `about_author_front` section card. Fetches the signed-in user's stored
// `author_bio` (with a lightweight inline editor when the bio isn't set
// yet) and pushes it into the section content. Reused across every book
// so authors don't paste the same bio into each new book.
// -----------------------------------------------------------------------------
function AuthorProfilePrefill({ isEmpty, onApply, testId }) {
  const [busy, setBusy] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [bio, setBio] = useState("");
  const [displayName, setDisplayName] = useState("");

  const fetchProfile = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/auth/me");
      setBio(data.author_bio || "");
      setDisplayName(data.author_display_name || data.name || "");
      return data;
    } catch (err) { toast.error(formatApiError(err)); return null; }
    finally { setBusy(false); }
  };

  const applyOrOpen = async () => {
    const me = await fetchProfile();
    if (!me) return;
    if ((me.author_bio || "").trim()) {
      onApply(me.author_bio, me.author_display_name || me.name);
    } else {
      // No bio saved yet — open the inline editor so the author can
      // write one, save it to their profile, and apply in one flow.
      setShowEditor(true);
    }
  };

  const saveAndApply = async () => {
    if (!bio.trim()) return toast.error("Write a short bio to save.");
    setBusy(true);
    try {
      const { data } = await api.patch("/auth/me", {
        author_bio: bio.trim(),
        ...(displayName.trim() ? { author_display_name: displayName.trim() } : {}),
      });
      onApply(data.author_bio, data.author_display_name || data.name);
      setShowEditor(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (showEditor) {
    return (
      <div className="flex-1 border border-[color:var(--primary)] bg-white p-2 space-y-2"
           data-testid={`${testId}-editor`}>
        <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
          Set your author profile once — it will prefill this section in every book you write.
        </p>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-widest mb-1 text-[color:var(--text-muted)]">Display name</span>
          <input value={displayName} onChange={(e)=>setDisplayName(e.target.value)}
                 placeholder="e.g. Dr. Jane Doe, RN"
                 className="w-full border border-[color:var(--border)] px-2 py-1 text-xs focus:outline-none focus:border-[color:var(--primary)]"
                 data-testid={`${testId}-display-name`}/>
        </label>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-widest mb-1 text-[color:var(--text-muted)]">Bio</span>
          <textarea rows={4} value={bio} onChange={(e)=>setBio(e.target.value)}
                    placeholder="Two or three sentences about you — credentials, background, why this book…"
                    className="w-full border border-[color:var(--border)] px-2 py-1 text-xs font-reading focus:outline-none focus:border-[color:var(--primary)]"
                    data-testid={`${testId}-bio`}/>
        </label>
        <div className="flex items-center gap-2">
          <button type="button" onClick={saveAndApply} disabled={busy}
                  className="text-xs px-2 py-1 bg-[color:var(--primary)] text-white"
                  data-testid={`${testId}-save`}>
            Save to profile &amp; prefill
          </button>
          <button type="button" onClick={()=>setShowEditor(false)}
                  className="text-xs px-2 py-1 border border-[color:var(--border)]"
                  data-testid={`${testId}-cancel`}>Cancel</button>
        </div>
      </div>
    );
  }

  // Default chip. Hidden if the section already has non-empty content —
  // we don't want to accidentally overwrite the author's edits.
  if (!isEmpty) return null;

  return (
    <button type="button" onClick={applyOrOpen} disabled={busy}
            className="text-xs inline-flex items-center gap-1 text-[color:var(--primary)] hover:underline"
            data-testid={testId}>
      <UserCog className="w-3 h-3"/>
      {busy ? "…" : "Prefill from your author profile"}
    </button>
  );
}
